import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../api/services';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('fastline_access_token');
      const savedUser = localStorage.getItem('fastline_user');

      if (token && savedUser) {
        try {
          setUser(JSON.parse(savedUser));
          setPermissions(['*']);
          // Attempt to refresh profile permissions in background
          authService.getMe().then((meData) => {
            if (meData && meData.user) {
              setUser(meData.user);
              if (meData.permissions) setPermissions(meData.permissions);
            }
          }).catch(() => {});
        } catch (err) {
          console.warn('Auth initialization warning:', err);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (userName, password) => {
    try {
      const data = await authService.login(userName, password);

      if (!data || !data.accessToken) {
        return { success: false, message: 'لم يتم استلام رمز التحقق من السيرفر' };
      }

      localStorage.setItem('fastline_access_token', data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem('fastline_refresh_token', data.refreshToken);
      }

      const profile = {
        userName: userName,
        displayName: data.company?.companyNameAr || data.company?.companyNameEn || userName,
        companyName: data.company?.companyNameAr || 'فاست لاين للتوصيل',
        companyId: data.company?.companyId,
        role: data.userType === 1 ? 'CompanyAdmin' : 'Staff',
      };

      localStorage.setItem('fastline_user', JSON.stringify(profile));
      setUser(profile);
      setPermissions(['*']);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      let errorMsg = 'فشل تسجيل الدخول، تحقق من اسم المستخدم وكلمة المرور';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        errorMsg = 'تعذر الاتصال بالخادم، يرجى التحقق من اتصال الإنترنت';
      }
      return { success: false, message: errorMsg };
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setPermissions([]);
  };

  const hasPermission = (permission) => {
    if (permissions.includes('*') || permissions.includes('SuperAdmin')) return true;
    return permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        loading,
        login,
        logout,
        hasPermission,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
